// @ts-strict-ignore
import { Columns, ProductCard } from "@fullscript/aviary-web";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@aviary";
import type { Sizes } from "@aviary/components/Columns/grid";
import { useProductAvailabilityLabel } from "@aviary/hooks";
import { useProductCardsColumnWidth } from "@clinic/emerson/hooks/useProductCardsColumnWidth/useProductCardsColumnWidth";
import { AddToCartButton } from "@emerson/components/AddToCartButton/AddToCartButton";
import { useCatalogProductLazyQuery } from "@emerson/data/queries/CatalogProductQuery/CatalogProduct.query";
import { l } from "@emerson/locales/i18n";
import { useFlippers } from "@shared/context/FlippersContext";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import {
  EmersonCheckoutStartEntryPoint,
  EmersonCheckoutAddToCartEntryPoint,
  EmersonViewProductEntryPoint,
} from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import type { EmersonEntryPoint } from "@shared/hooks/useAnalyticsLogger/EmersonAnalyticsEntryPoint";
import { BrandAgreementEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useAvailableVariantsWithCount } from "@shared/hooks/useAvailableVariantsWithCount/useAvailableVariantsWithCount";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useNavigate } from "@shared/react-router-dom/react-router-dom";
import {
  AvailabilityStatus,
  type AvailabilityStatusValueType,
} from "@shared/types/availabilityStatus";
import type { Product, Variant } from "@shared/types/graphqlGenerated";

import { QuickViewModal } from "./QuickViewModal/QuickViewModal";

type ProductWithOptionalCBD = Pick<
  Product,
  "id" | "name" | "variantCount" | "heatSensitive" | "shortShelfLife" | "eqpLevel" | "isFeatured"
> &
  Partial<Pick<Product, "containsCbd">>;

interface Props {
  product: ProductWithOptionalCBD;
  variant: Pick<
    Variant,
    | "id"
    | "availabilityStatus"
    | "expectedReturnDate"
    | "image"
    | "imageNew"
    | "descriptor"
    | "emersonRetailPrice"
    | "wholesalePrice"
    | "sku"
    | "isNew"
  >;
  brandName: string;
  to?: string;
  entryPoint: EmersonEntryPoint;
  checkoutStartEntryPoint?: EmersonCheckoutStartEntryPoint;
  checkoutAddToCartEntryPoint?: EmersonCheckoutAddToCartEntryPoint;
  searchRank?: number;
  isFeatured?: boolean;
  noFilterColumn?: boolean;
}

const EmersonProductCard = ({
  product,
  variant,
  brandName,
  to,
  entryPoint,
  checkoutStartEntryPoint = EmersonCheckoutStartEntryPoint.CATALOG_PRODUCTCARD_VIEWCART,
  checkoutAddToCartEntryPoint = EmersonCheckoutAddToCartEntryPoint.PRODUCT_CARD,
  searchRank,
  isFeatured,
  noFilterColumn,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const { isUnauthenticated } = useSharedGlobalConfig();
  const logAnalyticsEvent = useAnalyticsLogger();
  const [isProductPriceBlurred] = useFlippers("km_blurred_product_prices");
  const columnWidth = useProductCardsColumnWidth(noFilterColumn);
  const [currentVariantId, setCurrentVariantId] = useState<string>(variant?.id);
  const [productQuickView, setProductQuickView] = useState<string>(null);

  const [getProductWithAllVariants, { data }] = useCatalogProductLazyQuery({
    variables: { productId: product.id, isUnauthenticated },
  });

  const navigate = useNavigate();

  const goToPDP = (ifViewSimilarProduct = false) => {
    navigate(to, { state: { ifViewSimilarProduct } });
  };

  const { name } = product;

  const isSensitive = product.heatSensitive || product.shortShelfLife;
  const availableVariantsStatus = [
    AvailabilityStatus.UNAVAILABLE.toLowerCase(),
    AvailabilityStatus.DISCONTINUED.toLowerCase(),
    AvailabilityStatus.NA.toLowerCase(),
  ];
  const availableVariants: any = data?.viewer?.product?.variants ?? [variant];

  const currentVariant = availableVariants.find(({ id }) => id === currentVariantId) ?? variant;
  const { image, wholesalePrice } = currentVariant;
  const isCurrentVariantUnavailable = availableVariantsStatus.includes(
    currentVariant?.availabilityStatus?.toLowerCase()
  );

  const { expectedReturnDateText } = useProductAvailabilityLabel(
    currentVariant?.availabilityStatus as AvailabilityStatusValueType,
    currentVariant?.expectedReturnDate
  );

  const availableVariantsWithCount = useAvailableVariantsWithCount({
    variantCount: product.variantCount,
    variants: availableVariants,
    selectedVariant: currentVariant,
  });

  if (!variant) {
    return null;
  }

  const availableVariantsWithLabel = availableVariantsWithCount.map(_variant => ({
    ..._variant,
    availabilityStatusTooltipContent: expectedReturnDateText,
  }));

  const promotion = null;

  const handleVariantSelect = (variantId: string) => {
    setCurrentVariantId(variantId);
  };

  const onQuickViewClick = () => {
    setProductQuickView(product.id);
    logProductViewEvent(true);
  };

  const logProductViewEvent = (isQuickView: boolean = false) => {
    logAnalyticsEvent({
      eventName: isUnauthenticated
        ? AnalyticsEvent.EMERSON_UNAUTH_PRODUCT_VIEW
        : AnalyticsEvent.EMERSON_PRODUCT_VIEW,
      payload: {
        entry_point: isQuickView ? EmersonViewProductEntryPoint.QUICK_VIEW : entryPoint,
        default_variant: variant.sku,
        product_status: currentVariant.availabilityStatus?.toLowerCase(),
        sku: currentVariant.sku,
        brand: brandName,
        ...(typeof searchRank === "number" && { search_rank: searchRank }),
      },
    });
  };

  const pills = [
    isFeatured ? { pillContent: t(l.productDetails.Featured) } : null,
    currentVariant.isNew ? { pillContent: t(l.productDetails.New) } : null,
  ].filter(Boolean);

  const renderQuickViewModal = () => (
    <QuickViewModal
      isOpen={!!productQuickView}
      productId={productQuickView}
      onClose={() => setProductQuickView(null)}
    />
  );

  const renderCTA = () => (
    <AddToCartButton
      isFullWidth
      similarProductsEnabled
      containsHemp={product?.containsCbd}
      isCatalog
      isAnyVariantUnavailable={isCurrentVariantUnavailable}
      variantId={currentVariant?.id}
      availabilityStatus={currentVariant?.availabilityStatus}
      analyticsEntryPoint={checkoutStartEntryPoint}
      logProductViewEvent={logProductViewEvent}
      to={to}
      sku={currentVariant?.sku}
      productId={product.id}
      entryPoint={checkoutAddToCartEntryPoint}
      searchRank={searchRank}
      brandName={brandName}
      defaultVariant={variant?.sku}
      brandAgreementEntryPoint={BrandAgreementEntryPoint.PRODUCT_CARD}
      handleSimilarProductsClick={() => goToPDP(true)}
    />
  );

  const renderActions = () => {
    if (isUnauthenticated) {
      return (
        <Button intention="system" isFullWidth disabled>
          {t(l.catalog.LoginToViewPrice)}
        </Button>
      );
    }

    return renderCTA();
  };

  return (
    <Columns.Column
      key={currentVariantId}
      columnWidth={columnWidth as Sizes}
      data-e2e="emerson-product-card"
      data-testid="grid-product-card"
      {...rest}
    >
      <ProductCard
        isFullWidth
        onCardClick={logProductViewEvent}
        to={to}
        data-e2e={name + "-wrapper"}
        data-testid="go-to-pdp"
      >
        <ProductCard.TopBar pills={pills} qualityBadge={product.eqpLevel} />
        <ProductCard.Media image={image} size="large" onQuickViewClick={onQuickViewClick} />
        <ProductCard.Info
          name={name}
          brandOrAuthor={brandName}
          typographyStyleOverride="h4"
          isDoubleLine
        />
        <ProductCard.VariantDropdown
          currentVariantId={currentVariant.id}
          variants={availableVariantsWithLabel as any}
          onOpen={getProductWithAllVariants}
          onVariantSelect={handleVariantSelect}
          isContentLoading={availableVariants.length !== product.variantCount}
        />
        {!isUnauthenticated && (
          <ProductCard.Price
            isBold
            msrp={wholesalePrice}
            sellPrice={promotion?.price}
            type={isProductPriceBlurred ? "blurred" : "retail"}
          />
        )}
        <ProductCard.CoreActions>{renderActions()}</ProductCard.CoreActions>
        {!!isSensitive && (
          <ProductCard.BottomBar
            hasShortShelfLife={product?.shortShelfLife}
            isHeatSensitive={product?.heatSensitive}
          />
        )}
      </ProductCard>
      {renderQuickViewModal()}
    </Columns.Column>
  );
};
export { EmersonProductCard };
